import supabase from "@/services/supabaseClient.js";
import {
  readPharmacyProfile,
  resolvePharmacySettlementOwnerUserId,
} from "@/services/pharmacyLaneService.js";
import {
  normalizeInventoryPrice,
  resolveBranchCurrency,
} from "@/lib/pharmacyCurrencyIntegrity.mjs";

function pickFirst(...values) {
  for (const value of values) {
    const text = String(value ?? "").trim();
    if (text) return text;
  }
  return "";
}

function parseNumber(value) {
  const raw = String(value ?? "")
    .replace(/[^\d.-]/g, "")
    .trim();
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : 0;
}

function normalizeInventoryDate(value) {
  const raw = String(value ?? "").trim();
  if (!raw) return "";

  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;

  const dotted = raw.match(/^(\d{2})[./-](\d{2})[./-](\d{4})$/);
  if (dotted) {
    const [, dd, mm, yyyy] = dotted;
    return `${yyyy}-${mm}-${dd}`;
  }

  const slashFirst = raw.match(/^(\d{4})[./-](\d{2})[./-](\d{2})$/);
  if (slashFirst) {
    const [, yyyy, mm, dd] = slashFirst;
    return `${yyyy}-${mm}-${dd}`;
  }

  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) return raw;

  const yyyy = parsed.getFullYear();
  const mm = String(parsed.getMonth() + 1).padStart(2, "0");
  const dd = String(parsed.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function isIsoDate(value) {
  return /^\d{4}-\d{2}-\d{2}$/.test(String(value || "").trim());
}

function normalizeInventoryRow(row = {}, index = 0) {
  const item = pickFirst(row.item, row.item_name, row.itemName, row.medicine_name, row.product_name);
  const detail = pickFirst(row.detail, row.strength, row.pack_size);
  const stockId = pickFirst(row.stockId, row.stock_id, row.sku, row.item_code, `INV-${index + 1}`);
  const currentStock = parseNumber(row.currentStock ?? row.current_stock ?? row.quantity_on_hand);
  const minimalStock = parseNumber(row.minimalStock ?? row.minimal_stock ?? row.reorder_level);
  const price = normalizeInventoryPrice(row, row.currency || "ZAR");
  const unitPriceValue = Number(price.amount || 0);
  const explicitImageUrl = pickFirst(
    row.imageUrl,
    row.image_url,
    row.photoUrl,
    row.pictureUrl,
    row.image,
  );

  return {
    id: pickFirst(row.id, stockId, `${item || "item"}-${index + 1}`),
    item: item || "Medicine",
    detail,
    category: pickFirst(row.category, row.classification),
    stockId,
    currentStock,
    minimalStock,
    unitPriceValue,
    unitPrice: unitPriceValue ? unitPriceValue.toFixed(2) : pickFirst(row.unitPrice, row.unit_price, row.retail_price, row.price),
    currency: price.currency,
    supplier: pickFirst(row.supplier, row.vendor, row.supplier_name),
    branch: pickFirst(row.branch, row.branch_name, row.location),
    batchNumber: pickFirst(row.batchNumber, row.batch_number),
    expiryDate: pickFirst(row.expiryDate, row.expiry_date),
    binLocation: pickFirst(row.binLocation, row.bin_location),
    imageUrl: explicitImageUrl,
    image: explicitImageUrl || "/icons/pharmacy/stock-placeholder.svg",
    status: pickFirst(row.status, "active"),
  };
}

function toDbPayload({ ownerUserId = "", currentUserId = "", item = {}, profile = null }) {
  const currency = resolveBranchCurrency({
    explicitCurrency: item.currency,
    profile,
    fallbackCurrency: "ZAR",
  });

  return {
    pharmacy_owner_user_id: String(ownerUserId || "").trim(),
    created_by_user_id: String(currentUserId || "").trim() || null,
    updated_by_user_id: String(currentUserId || "").trim() || null,
    item_name: pickFirst(item.item, item.itemName),
    detail: pickFirst(item.detail),
    category: pickFirst(item.category),
    stock_id: pickFirst(item.stockId, item.stock_id),
    quantity_on_hand: Math.max(0, Math.round(parseNumber(item.currentStock ?? item.quantity_on_hand))),
    reorder_level: Math.max(0, Math.round(parseNumber(item.minimalStock ?? item.reorder_level))),
    unit_price_cents: Math.max(0, Math.round(parseNumber(item.unitPrice ?? item.unit_price) * 100)),
    currency,
    supplier: pickFirst(item.supplier),
    branch_name: pickFirst(item.branch, profile?.pharmacyName, profile?.tradingName),
    batch_number: pickFirst(item.batchNumber, item.batch_number),
    expiry_date:
      normalizeInventoryDate(pickFirst(item.expiryDate, item.expiry_date)) || null,
    bin_location: pickFirst(item.binLocation, item.bin_location),
    image_url: pickFirst(item.imageUrl, item.image_url) || null,
    status: pickFirst(item.status, "active"),
  };
}

async function resolveInventoryOwner({ userId = "", email = "", profile = null } = {}) {
  const cleanUserId = String(userId || "").trim();
  const cleanEmail = String(email || "").trim().toLowerCase();
  const ownerUserId = await resolvePharmacySettlementOwnerUserId({
    userId: cleanUserId,
    email: cleanEmail,
  });

  const resolvedProfile = profile || readPharmacyProfile() || {};

  return {
    ownerUserId: String(ownerUserId || "").trim(),
    profile: resolvedProfile,
  };
}

async function seedLegacyInventoryIfNeeded({ ownerUserId = "", currentUserId = "", profile = null } = {}) {
  const legacyItems = Array.isArray(profile?.inventoryItems) ? profile.inventoryItems : [];
  if (!ownerUserId || !legacyItems.length) return null;

  const { count, error: countError } = await supabase
    .from("pharmacy_inventory_items")
    .select("id", { head: true, count: "exact" })
    .eq("pharmacy_owner_user_id", ownerUserId);

  if (countError) throw countError;
  if (Number(count || 0) > 0) return null;

  const payload = legacyItems
    .map((item) =>
      toDbPayload({
        ownerUserId,
        currentUserId,
        item,
        profile,
      }),
    )
    .filter((item) => item.item_name && item.stock_id);

  if (!payload.length) return null;

  const { error } = await supabase
    .from("pharmacy_inventory_items")
    .upsert(payload, {
      onConflict: "pharmacy_owner_user_id,stock_id",
    });

  if (error) throw error;
  return true;
}

export async function listPharmacyInventoryItems({ userId = "", email = "", profile = null } = {}) {
  const { ownerUserId, profile: resolvedProfile } = await resolveInventoryOwner({ userId, email, profile });
  if (!ownerUserId) return [];

  await seedLegacyInventoryIfNeeded({
    ownerUserId,
    currentUserId: userId,
    profile: resolvedProfile,
  });

  const { data, error } = await supabase
    .from("pharmacy_inventory_items")
    .select("*")
    .eq("pharmacy_owner_user_id", ownerUserId)
    .eq("status", "active")
    .order("updated_at", { ascending: false })
    .order("created_at", { ascending: false });

  if (error) throw error;
  return (Array.isArray(data) ? data : []).map((row, index) => normalizeInventoryRow(row, index));
}

export async function getPharmacyInventoryItem({ userId = "", email = "", itemId = "" } = {}) {
  const cleanItemId = String(itemId || "").trim();
  if (!cleanItemId) return null;

  const { ownerUserId } = await resolveInventoryOwner({ userId, email });
  if (!ownerUserId) return null;

  const { data, error } = await supabase
    .from("pharmacy_inventory_items")
    .select("*")
    .eq("id", cleanItemId)
    .eq("pharmacy_owner_user_id", ownerUserId)
    .maybeSingle();

  if (error) throw error;
  return data ? normalizeInventoryRow(data, 0) : null;
}

export async function savePharmacyInventoryItem({ userId = "", email = "", item = {}, profile = null } = {}) {
  const { ownerUserId, profile: resolvedProfile } = await resolveInventoryOwner({ userId, email, profile });
  if (!ownerUserId) {
    throw new Error("Sign in with a linked pharmacy owner or accepted branch member account to manage inventory.");
  }

  const cleanUserId = String(userId || "").trim();
  const cleanId = String(item?.id || "").trim();
  const payload = toDbPayload({
    ownerUserId,
    currentUserId: cleanUserId,
    item,
    profile: resolvedProfile,
  });

  if (!payload.item_name || !payload.stock_id) {
    throw new Error("Item name and stock ID are required.");
  }

  if (payload.expiry_date && !isIsoDate(payload.expiry_date)) {
    throw new Error("Expiry date must be a real date like 2028-12-31 or 31.12.2028.");
  }

  let query = supabase
    .from("pharmacy_inventory_items");

  if (cleanId) {
    const updatePayload = {
      ...payload,
      updated_at: new Date().toISOString(),
    };
    delete updatePayload.created_by_user_id;
    const { data, error } = await query
      .update(updatePayload)
      .eq("id", cleanId)
      .eq("pharmacy_owner_user_id", ownerUserId)
      .select("*")
      .single();
    if (error) throw error;
    return normalizeInventoryRow(data, 0);
  }

  const { data, error } = await query
    .insert(payload)
    .select("*")
    .single();

  if (error) throw error;
  return normalizeInventoryRow(data, 0);
}

export async function deletePharmacyInventoryItem({ userId = "", email = "", itemId = "" } = {}) {
  const cleanItemId = String(itemId || "").trim();
  if (!cleanItemId) return;

  const { ownerUserId } = await resolveInventoryOwner({ userId, email });
  if (!ownerUserId) {
    throw new Error("Sign in with a linked pharmacy owner or accepted branch member account to manage inventory.");
  }

  const { error } = await supabase
    .from("pharmacy_inventory_items")
    .delete()
    .eq("id", cleanItemId)
    .eq("pharmacy_owner_user_id", ownerUserId);

  if (error) throw error;
}
